/**
 * Feature Quick Panel
 * 
 * Compact version of feature manager for embedding in DevTools.
 * Shows current whitelist state and quick toggles.
 */

import React, { useState } from 'react';
import {
  FeatureMode,
  useFeatureMode,
  useFeatureWhitelist,
  useFeatureActions,
  useEnabledFeatures,
} from '../../../../core/features';
import { FeatureManagerWindow } from './FeatureManagerWindow';

interface FeatureQuickPanelProps {
  domain: string;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '12px',
  },
  modeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 10px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '12px',
  },
  whitelistContainer: {
    marginBottom: '16px',
  },
  whitelistTitle: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '8px',
  },
  whitelistItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 10px',
    background: 'rgba(241, 196, 15, 0.1)',
    border: '1px solid rgba(241, 196, 15, 0.2)',
    borderRadius: '6px',
    marginBottom: '6px',
    fontSize: '12px',
  },
  emptyWhitelist: {
    padding: '16px',
    textAlign: 'center',
    color: '#666',
    fontSize: '12px',
    fontStyle: 'italic',
    background: 'rgba(255,255,255,0.02)',
    borderRadius: '6px',
  },
  actionRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px',
  },
  button: {
    flex: 1,
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid rgba(255,255,255,0.1)',
    background: 'rgba(255,255,255,0.05)',
    color: '#aaa',
    fontSize: '11px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
  },
  buttonPrimary: {
    background: 'rgba(52, 152, 219, 0.2)',
    borderColor: 'rgba(52, 152, 219, 0.3)',
    color: '#3498db',
  },
  buttonActive: {
    background: 'rgba(46, 204, 113, 0.2)',
    borderColor: 'rgba(46, 204, 113, 0.3)',
    color: '#2ecc71',
  },
  buttonWarning: {
    background: 'rgba(241, 196, 15, 0.2)',
    borderColor: 'rgba(241, 196, 15, 0.3)',
    color: '#f1c40f',
  },
  buttonDanger: {
    background: 'rgba(231, 76, 60, 0.2)',
    borderColor: 'rgba(231, 76, 60, 0.3)',
    color: '#e74c3c',
  },
  featureGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '6px',
    marginTop: '12px',
  },
  featureChip: {
    padding: '6px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    background: 'rgba(46, 204, 113, 0.1)',
    border: '1px solid rgba(46, 204, 113, 0.2)',
    color: '#2ecc71',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  featureChipHidden: {
    background: 'rgba(255,255,255,0.03)',
    borderColor: 'rgba(255,255,255,0.05)',
    color: '#666',
  },
  quickActions: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '6px',
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: '1px solid rgba(255,255,255,0.05)',
  },
  helpText: {
    fontSize: '10px',
    color: '#666',
    marginTop: '12px',
    padding: '8px',
    background: 'rgba(255,255,255,0.02)',
    borderRadius: '4px',
    lineHeight: 1.5,
  },
};

export const FeatureQuickPanel: React.FC<FeatureQuickPanelProps> = ({ domain }) => {
  const mode = useFeatureMode(domain);
  const whitelist = useFeatureWhitelist(domain);
  const enabledFeatures = useEnabledFeatures(domain);
  const { setMode, whitelist: addToWhitelist, unwhitelist, clearWhitelist } = useFeatureActions();
  const [showManager, setShowManager] = useState(false);

  const getModeStyle = () => {
    switch (mode) {
      case FeatureMode.NORMAL:
        return { background: 'rgba(46, 204, 113, 0.1)', color: '#2ecc71' };
      case FeatureMode.WHITELIST:
        return { background: 'rgba(241, 196, 15, 0.1)', color: '#f1c40f' };
      case FeatureMode.DISABLED:
        return { background: 'rgba(231, 76, 60, 0.1)', color: '#e74c3c' };
      default:
        return { background: 'rgba(255,255,255,0.1)', color: '#888' };
    }
  };

  const handleClearWhitelist = () => {
    clearWhitelist(domain);
  };

  const handleEnableAll = () => {
    setMode(domain, FeatureMode.NORMAL);
  };

  const handleWhitelistMode = () => {
    setMode(domain, FeatureMode.WHITELIST);
  };

  return (
    <>
      <div style={styles.container}>
        {/* Mode Badge */}
        <div style={{ ...styles.modeBadge, ...getModeStyle() }}>
          <span>
            {mode === FeatureMode.NORMAL && '✓'}
            {mode === FeatureMode.WHITELIST && '🧪'}
            {mode === FeatureMode.DISABLED && '⊘'}
          </span>
          <span>{mode} Mode</span>
          <span style={{ opacity: 0.6 }}>|</span>
          <span>{enabledFeatures.length} visible</span>
        </div>

        {/* Quick Actions */}
        <div style={styles.actionRow}>
          <button
            style={{
              ...styles.button,
              ...(mode === FeatureMode.NORMAL ? styles.buttonActive : {}),
            }}
            onClick={handleEnableAll}
          >
            <span>✓</span>
            All
          </button>
          <button
            style={{
              ...styles.button,
              ...(mode === FeatureMode.WHITELIST ? styles.buttonWarning : {}),
            }}
            onClick={handleWhitelistMode}
          >
            <span>🧪</span>
            Test
          </button>
          <button
            style={{ ...styles.button, ...styles.buttonPrimary }}
            onClick={() => setShowManager(true)}
          >
            <span>⚙️</span>
            Manage
          </button>
        </div>

        {/* Whitelist Section */}
        {mode === FeatureMode.WHITELIST && (
          <div style={styles.whitelistContainer}>
            <div style={styles.whitelistTitle}>Whitelisted Features</div>
            {whitelist.length === 0 ? (
              <div style={styles.emptyWhitelist}>
                No features whitelisted
                <br />
                <span style={{ fontSize: '10px' }}>Click "Manage" to add features</span>
              </div>
            ) : (
              <>
                {whitelist.map((feature: string) => (
                  <div key={feature} style={styles.whitelistItem}>
                    <span>★ {feature}</span>
                    <button
                      onClick={() => unwhitelist(domain, feature)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#e74c3c',
                        cursor: 'pointer',
                        fontSize: '14px',
                        padding: '0 4px',
                      }}
                      title="Remove from whitelist"
                    >
                      ×
                    </button>
                  </div>
                ))}
                <button
                  style={{ ...styles.button, ...styles.buttonDanger, marginTop: '8px' }}
                  onClick={handleClearWhitelist}
                >
                  <span>🗑</span>
                  Clear All
                </button>
              </>
            )}
          </div>
        )}

        {/* Quick Add (shown in whitelist mode) */}
        {mode === FeatureMode.WHITELIST && whitelist.length > 0 && (
          <>
            <div style={{ ...styles.whitelistTitle, marginTop: '16px' }}>
              Recently Hidden
            </div>
            <div style={styles.featureGrid}>
              {enabledFeatures
                .filter((f: string) => !whitelist.includes(f))
                .slice(0, 4)
                .map((feature: string) => (
                  <button
                    key={feature}
                    style={{ ...styles.featureChip, ...styles.featureChipHidden }}
                    onClick={() => addToWhitelist(domain, feature)}
                    title="Add to whitelist"
                  >
                    <span>☆ {feature}</span>
                    <span>+</span>
                  </button>
                ))}
            </div>
          </>
        )}

        {/* Preset Actions */}
        <div style={styles.quickActions}>
          <button
            style={styles.button}
            onClick={() => {
              // Solo heal button for quick testing
              addToWhitelist(domain, 'healButton');
              setMode(domain, FeatureMode.WHITELIST);
            }}
            title="Test only heal button"
          >
            🏥 Heal Only
          </button>
          <button
            style={styles.button}
            onClick={() => {
              // Solo combat features
              addToWhitelist(domain, 'addXpButton', 'levelUpButton');
              setMode(domain, FeatureMode.WHITELIST);
            }}
            title="Test leveling features"
          >
            ⬆️ Level Only
          </button>
          <button
            style={styles.button}
            onClick={() => {
              // Solo cosmetic
              addToWhitelist(domain, 'changeColor', 'randomizeLook');
              setMode(domain, FeatureMode.WHITELIST);
            }}
            title="Test appearance features"
          >
            🎨 Look Only
          </button>
        </div>

        {/* Help */}
        <div style={styles.helpText}>
          <strong>Normal:</strong> Show all enabled features
          <br />
          <strong>Test:</strong> Show only whitelisted features
          <br />
          <strong>Manage:</strong> Full feature manager
        </div>
      </div>

      {/* Full Manager Window */}
      {showManager && (
        <FeatureManagerWindow onClose={() => setShowManager(false)} />
      )}
    </>
  );
};
